import React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

/**
 * Alert/Callout - Feedback messages, warnings, info boxes
 * 
 * API inspirowane Radix Themes Callout
 * - Rozmiary: "1" | "2" | "3"
 * - Variants: "soft" | "surface" | "outline"
 * - Color: "gray" | "pink" | "success" | "danger" | "warning"
 * 
 * @example
 * ```tsx
 * <Alert variant="soft" color="success">
 *   <AlertTitle>Sukces!</AlertTitle>
 *   <AlertDescription>Operacja zakończona pomyślnie</AlertDescription>
 * </Alert>
 * 
 * <Alert variant="outline" color="danger" icon={<AlertCircle />}>
 *   <AlertDescription>Wystąpił błąd</AlertDescription>
 * </Alert>
 * ```
 */

const alertVariants = cva(
  cn(
    "relative w-full rounded-[var(--radius-md)]",
    "transition-colors duration-150"
  ),
  {
    variants: {
      variant: {
        // Soft - subtelne kolorowe tło
        soft: "",
        // Surface - neutralne tło z borderem
        surface: cn(
          "bg-[var(--dark-2)]",
          "border"
        ),
        // Outline - transparentne z borderem
        outline: cn(
          "bg-transparent",
          "border"
        ),
      },
      color: {
        gray: "",
        pink: "",
        success: "",
        danger: "",
        warning: "",
      },
      size: {
        "1": cn(
          "p-[var(--space-3)]",
          "gap-[var(--space-2)]"
        ),
        "2": cn(
          "p-[var(--space-4)]",
          "gap-[var(--space-3)]"
        ),
        "3": cn(
          "p-[var(--space-5)]",
          "gap-[var(--space-4)]"
        ),
      },
    },
    compoundVariants: [
      // Gray
      {
        variant: "soft",
        color: "gray",
        className: "bg-[var(--gray-a3)] text-[var(--gray-12)]",
      },
      {
        variant: ["surface", "outline"],
        color: "gray",
        className: "border-[var(--gray-a6)] text-[var(--gray-12)]",
      },
      // Pink
      {
        variant: "soft",
        color: "pink",
        className: "bg-[var(--pink-a3)] text-[var(--pink-12)]",
      },
      {
        variant: ["surface", "outline"],
        color: "pink",
        className: "border-[var(--pink-a6)] text-[var(--pink-12)]",
      },
      // Success
      {
        variant: "soft",
        color: "success",
        className: "bg-[var(--success-a3)] text-[var(--success-12)]",
      },
      {
        variant: ["surface", "outline"],
        color: "success",
        className: "border-[var(--success-a6)] text-[var(--success-12)]",
      },
      // Danger
      {
        variant: "soft",
        color: "danger",
        className: "bg-[var(--danger-a3)] text-[var(--danger-12)]",
      },
      {
        variant: ["surface", "outline"],
        color: "danger",
        className: "border-[var(--danger-a6)] text-[var(--danger-12)]",
      },
      // Warning
      {
        variant: "soft",
        color: "warning",
        className: "bg-[var(--warning-a3)] text-[var(--warning-12)]",
      },
      {
        variant: ["surface", "outline"],
        color: "warning",
        className: "border-[var(--warning-a6)] text-[var(--warning-12)]",
      },
    ],
    defaultVariants: {
      variant: "soft",
      color: "gray",
      size: "2",
    },
  }
)

export interface AlertProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof alertVariants> {
  /**
   * Icon to display
   */
  icon?: React.ReactNode
}

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  ({ className, variant, color, size, icon, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        role="alert"
        className={cn(
          alertVariants({ variant, color, size }),
          "flex items-start",
          className
        )}
        {...props}
      >
        {icon && (
          <div className="shrink-0 mt-0.5 [&_svg]:h-5 [&_svg]:w-5">
            {icon}
          </div>
        )}
        <div className="flex-1 space-y-1">{children}</div>
      </div>
    )
  }
)
Alert.displayName = "Alert"

/**
 * AlertTitle - Title for Alert
 */
const AlertTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h5
    ref={ref}
    className={cn(
      "text-[var(--font-size-3)] font-[var(--font-weight-semibold)]",
      "leading-[var(--line-height-2)]",
      "tracking-[var(--letter-spacing-tight)]",
      className
    )}
    {...props}
  />
))
AlertTitle.displayName = "AlertTitle"

/**
 * AlertDescription - Description for Alert
 */
const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn(
      "text-[var(--font-size-2)]",
      "leading-[var(--line-height-4)]",
      "opacity-90",
      className
    )}
    {...props}
  />
))
AlertDescription.displayName = "AlertDescription"

export { Alert, AlertTitle, AlertDescription, alertVariants }

